import * as React from 'react'
import { API, IDevice, IPerson, IWorkspaceMember } from '../../services/api'
import AlbumModel from '../../models/album'
import { countActiveFilters, GalleryFilters, GalleryFilterType } from './galleryFilterParams'

interface UseFiltersDrawerOptions {
  isOpen: boolean
  workspaceId: string
  filters: GalleryFilters
  onApply: (next: GalleryFilters) => void
  onClose: () => void
}

export interface UseFiltersDrawerResult {
  draft: GalleryFilters
  setQuery: (query: string) => void
  setAlbum: (album: AlbumModel | undefined) => void
  selectedAlbum: AlbumModel | undefined
  selectedPersons: Array<IPerson>
  addPerson: (person: IPerson) => void
  removePerson: (id: string) => void
  members: Array<IWorkspaceMember>
  selectedMembers: Array<IWorkspaceMember>
  addOwner: (id: string) => void
  removeOwner: (id: string) => void
  devices: Array<IDevice>
  setDeviceId: (id: string | undefined) => void
  /** selecting the active type again clears it */
  toggleType: (type: GalleryFilterType) => void
  setDateFrom: (day: string | undefined) => void
  setDateTo: (day: string | undefined) => void
  optionsLoading: boolean
  canClear: boolean
  clearAll: () => void
  apply: () => void
}

const addUnique = (list: Array<string> | undefined, id: string): Array<string> =>
  list?.includes(id) ? list : [...(list ?? []), id]

const removeId = (list: Array<string> | undefined, id: string): Array<string> | undefined => {
  const next = list?.filter((item) => item !== id)
  return next?.length ? next : undefined
}

export const useFiltersDrawer = ({
  isOpen,
  workspaceId,
  filters,
  onApply,
  onClose,
}: UseFiltersDrawerOptions): UseFiltersDrawerResult => {
  const [draft, setDraft] = React.useState<GalleryFilters>(filters)
  // persons are searched server-side on demand; this caches every person seen (picked
  // or resolved from the URL) so chips always have a name and avatar
  const [knownPersons, setKnownPersons] = React.useState<Record<string, IPerson>>({})
  const [members, setMembers] = React.useState<Array<IWorkspaceMember>>([])
  const [devices, setDevices] = React.useState<Array<IDevice>>([])
  const [selectedAlbum, setSelectedAlbum] = React.useState<AlbumModel | undefined>(undefined)
  const [optionsLoading, setOptionsLoading] = React.useState(false)
  const optionsLoadedRef = React.useRef<string | null>(null)
  const personFetchesRef = React.useRef<Set<string>>(new Set())

  // the drawer edits a draft, re-seeded from the applied filters on every open
  React.useEffect(() => {
    if (isOpen) setDraft(filters)
  }, [isOpen])

  // devices and members are small per-workspace lists — loaded once on first open;
  // persons and albums are searched server-side as the user types
  React.useEffect(() => {
    if (!isOpen || optionsLoadedRef.current === workspaceId) return
    optionsLoadedRef.current = workspaceId
    setOptionsLoading(true)

    const api = API.getInstance()
    Promise.all([api.getDevices({ workspaceId }), api.getWorkspace(workspaceId)])
      .then(([devicesRes, workspaceRes]) => {
        if (devicesRes.ok && devicesRes.data) setDevices(devicesRes.data)
        if (workspaceRes.ok && workspaceRes.data) setMembers(workspaceRes.data.users ?? [])
      })
      .finally(() => setOptionsLoading(false))
  }, [isOpen, workspaceId])

  // resolve person names for ids restored from the URL
  React.useEffect(() => {
    if (!isOpen) return
    const missing = (draft.personIds ?? []).filter(
      (id) => !knownPersons[id] && !personFetchesRef.current.has(id)
    )
    for (const id of missing) {
      personFetchesRef.current.add(id)
      API.getInstance()
        .getPerson({ workspaceId, id })
        .then((res) => {
          if (res.ok && res.data) {
            const person = res.data
            setKnownPersons((prev) => ({ ...prev, [person.id]: person }))
          }
        })
    }
  }, [isOpen, draft.personIds, workspaceId])

  // resolve the album label when the draft carries an id restored from the URL
  React.useEffect(() => {
    if (!isOpen || !draft.albumId) return
    if (selectedAlbum?.id === draft.albumId) return
    API.getInstance()
      .getAlbum({ workspaceId, id: draft.albumId })
      .then((res) => {
        if (res.ok && res.data) setSelectedAlbum(res.data)
      })
  }, [isOpen, draft.albumId, workspaceId])

  const patchDraft = (patch: Partial<GalleryFilters>) =>
    setDraft((prev) => ({ ...prev, ...patch }))

  const setAlbum = (album: AlbumModel | undefined) => {
    setSelectedAlbum(album)
    patchDraft({ albumId: album?.id })
  }

  // placeholder entries keep chips renderable while resolution is still in flight
  const selectedPersons = React.useMemo(
    () =>
      (draft.personIds ?? []).map(
        (id): IPerson => knownPersons[id] ?? { id, name: '…', workspace_id: workspaceId }
      ),
    [draft.personIds, knownPersons, workspaceId]
  )

  const selectedMembers = React.useMemo(
    () =>
      (draft.ownerIds ?? []).map((id): IWorkspaceMember => {
        const member = members.find((m) => m.id === id)
        return member ?? { id, first_name: '…', last_name: '', color: '#9ca3af' }
      }),
    [draft.ownerIds, members]
  )

  return {
    draft,
    setQuery: (query) => patchDraft({ query: query || undefined }),
    setAlbum,
    selectedAlbum: draft.albumId ? selectedAlbum : undefined,
    selectedPersons,
    addPerson: (person) => {
      setKnownPersons((prev) => ({ ...prev, [person.id]: person }))
      patchDraft({ personIds: addUnique(draft.personIds, person.id) })
    },
    removePerson: (id) => patchDraft({ personIds: removeId(draft.personIds, id) }),
    members,
    selectedMembers,
    addOwner: (id) => patchDraft({ ownerIds: addUnique(draft.ownerIds, id) }),
    removeOwner: (id) => patchDraft({ ownerIds: removeId(draft.ownerIds, id) }),
    devices,
    setDeviceId: (id) => patchDraft({ deviceId: id }),
    toggleType: (type) => patchDraft({ type: draft.type === type ? undefined : type }),
    setDateFrom: (day) => patchDraft({ dateFrom: day || undefined }),
    setDateTo: (day) => patchDraft({ dateTo: day || undefined }),
    optionsLoading,
    canClear: countActiveFilters(draft) > 0,
    clearAll: () => setDraft({}),
    apply: () => {
      onApply(draft)
      onClose()
    },
  }
}
